import React, {
  useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { resolvedOptions, resolvedOptionsYesNo } from 'src/components/Cases/utils/selectOptions';
import * as CaseCloseHelper from 'src/components/Cases/utils/caseCloseHelpers';
import { closeServiceCase } from 'actions/Case/Contact/Group';
import { fetchGroupCaseOutcomes } from 'actions/Outcome/ServiceType/Group';
import { fetchNetworkCaseOutcomes } from 'actions/Outcome/ServiceType/Network';
import { get, isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import today from 'src/common/utils/today';
import { SimpleSelectField } from '@unite-us/app-components';
import { OverlaySpinner, Spinner } from 'common/spinners';
import getClosingParams from 'src/common/utils/Closing/getClosingParams';
import getOutcomesForServiceType from 'src/common/utils/Outcomes/utils/getOutcomesForServiceType';
import {
  crtb1349VariableAWebApp as crtb1349VariableAWebAppSelector,
  crtb1350VariableBWebApp as crtb1350VariableBWebAppSelector,
  crtb1351VariableCWebApp as crtb1351VariableCWebAppSelector,
} from 'common/utils/FeatureFlags/flags';
import minOpenCaseDate from 'common/utils/minOpenCaseDate';
import 'react-loading-skeleton/dist/skeleton.css';
import CaseCloseFields from './CaseCloseFormFields/CaseCloseFields';
import './CaseCloseForm.scss';

const REQUIRED_ERROR = 'Required';

const getFieldLabels = ({
  crtb1349VariableAWebApp,
  crtb1351VariableCWebApp,
  isOON,
  clientName,
}) => {
  const areVariablesON = crtb1349VariableAWebApp || crtb1351VariableCWebApp;
  const useQuestionLabels = areVariablesON && !isOON;

  return {
    resolvedLabel: useQuestionLabels ? `Did ${clientName} receive help?` : 'Is Resolved?',
    isResolvedOptions: useQuestionLabels ? resolvedOptionsYesNo : resolvedOptions,
    outcomeLabel: useQuestionLabels ? 'What was the outcome?' : 'Outcome',
  };
};

const buildValidationErrors = (values, showOpenAndClosedDateInputs) => {
  const errors = {};

  if (!values.resolved) errors.resolved = REQUIRED_ERROR;
  if (!values.outcome_id) errors.outcome_id = REQUIRED_ERROR;
  if (!values.note) errors.note = REQUIRED_ERROR;
  if (!values.exited_at) errors.exited_at = REQUIRED_ERROR;
  if (showOpenAndClosedDateInputs && !values.opened_date) errors.opened_date = REQUIRED_ERROR;

  return errors;
};

const fetchOutcomesForCase = ({
  caseToClose,
  serviceTypeId,
  fetchNetworkOutcomes,
  fetchGroupOutcomes,
}) => {
  if (isEmpty(serviceTypeId) || !caseToClose) {
    return;
  }

  const applicableStates = [caseToClose.state];
  if (caseToClose.network) {
    const networkId = get(caseToClose, 'network.id');
    fetchNetworkOutcomes(networkId, serviceTypeId, { applicable_states: applicableStates });
    return;
  }

  const caseGroupId = get(caseToClose, 'group.id');
  fetchGroupOutcomes(caseGroupId, serviceTypeId, { applicable_states: applicableStates });
};

export const CaseCloseForm = forwardRef((props, ref) => {
  const {
    caseToClose,
    closeDialog,
    contact,
    contactCases,
    groupId,
    invalidateQueries,
    outcomes,
    serviceTypeId,
    crtb1349VariableAWebApp,
    crtb1350VariableBWebApp,
    crtb1351VariableCWebApp,
    currentProviderId,
    initialValues,
    onSubmittingChange,
    // Redux actions
    closeServiceCase: closeCase,
    fetchGroupCaseOutcomes: fetchGroupOutcomes,
    fetchNetworkCaseOutcomes: fetchNetworkOutcomes,
  } = props;

  const [showSkeleton, setShowSkeleton] = useState(false);
  const [showFields, setShowFields] = useState(false);
  const [previousResolvedValue, setPreviousResolvedValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formHandleSubmitRef = useRef(null);

  const clientName = useMemo(() => get(contact, 'full_name', 'client name not found'), [contact]);

  const isOON = get(caseToClose, 'state') === 'off_platform';
  const isInternalCase = isEqual(currentProviderId, caseToClose?.provider?.id) && !isOON;
  const showVariableBorCNotOON = !isOON && (crtb1350VariableBWebApp || crtb1351VariableCWebApp);
  const showVariableBOON = isOON && crtb1350VariableBWebApp;
  const useStagedOutcomeFlow = showVariableBorCNotOON || showVariableBOON;
  const shouldFixModalSize = (crtb1350VariableBWebApp || crtb1351VariableCWebApp) && !showFields;
  const showOpenAndClosedDateInputs = isOON || isInternalCase;
  const showExitedAtField = CaseCloseHelper.showExitedAt(contactCases, get(caseToClose, 'id'));
  const minOpenedDate = minOpenCaseDate();
  const minDate = get(caseToClose, 'opened_date') !== null ?
    moment.unix(get(caseToClose, 'opened_date') - 1).format('MM/DD/YYYY') :
    null;

  // Fetch outcomes on mount
  useEffect(() => {
    fetchOutcomesForCase({
      caseToClose,
      serviceTypeId,
      fetchNetworkOutcomes,
      fetchGroupOutcomes,
    });
  }, [serviceTypeId, caseToClose, fetchNetworkOutcomes, fetchGroupOutcomes]);

  const { resolvedLabel, isResolvedOptions, outcomeLabel } = useMemo(
    () => getFieldLabels({
      crtb1349VariableAWebApp,
      crtb1351VariableCWebApp,
      isOON,
      clientName,
    }),
    [crtb1349VariableAWebApp, crtb1351VariableCWebApp, isOON, clientName],
  );

  const handleSubmit = useCallback(async (values) => {
    const closingParams = getClosingParams(values);

    await closeCase(groupId, contact.id, caseToClose, closingParams);
    invalidateQueries('notes');
  }, [groupId, contact, caseToClose, closeCase, invalidateQueries]);

  const handleCancel = useCallback(() => {
    closeDialog();
    setShowSkeleton(false);
    setShowFields(false);
  }, [closeDialog]);

  useImperativeHandle(ref, () => ({
    submit: () => formHandleSubmitRef.current?.(),
    cancel: handleCancel,
  }), [handleCancel]);

  useEffect(() => {
    if (onSubmittingChange) {
      onSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onSubmittingChange]);

  // Handle resolved value change for skeleton animation
  const handleResolvedChange = useCallback((newValue, form) => {
    if (newValue !== previousResolvedValue && newValue) {
      setShowSkeleton(true);
      setShowFields(false);
      form.change('outcome_id', undefined);
      setTimeout(() => {
        setShowSkeleton(false);
        setShowFields(true);
      }, 500);
    }
    setPreviousResolvedValue(newValue);
  }, [previousResolvedValue]);

  if (isEmpty(contact) || isEmpty(caseToClose)) {
    return <Spinner />;
  }

  const validate = useCallback(
    (values) => buildValidationErrors(values, showOpenAndClosedDateInputs),
    [showOpenAndClosedDateInputs],
  );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validate={validate}
      render={({
        handleSubmit: formHandleSubmit,
        submitting,
        values,
        form,
      }) => {
        formHandleSubmitRef.current = formHandleSubmit;
        if (submitting !== isSubmitting) {
          setIsSubmitting(submitting);
        }

        return (
          <form
            onSubmit={formHandleSubmit}
            className="content-with-actions"
          >
            <div className="content-container">
              <OverlaySpinner text="Closing Case..." show={submitting} />
              <div className={shouldFixModalSize ? 'fix-modal-size' : ''}>
                <Field name="resolved">
                  {({ input, meta }) => (
                    <SimpleSelectField
                      id="resolvedInput"
                      label={resolvedLabel}
                      options={isResolvedOptions}
                      value={input.value}
                      onChange={(value) => {
                        input.onChange(value);
                        handleResolvedChange(value, form);
                      }}
                      onBlur={input.onBlur}
                      required
                      error={meta.touched && meta.error ? meta.error : undefined}
                      searchable={false}
                    />
                  )}
                </Field>

                <CaseCloseFields
                  minDate={minDate}
                  minOpenedDate={minOpenedDate}
                  outcomeLabel={outcomeLabel}
                  outcomes={outcomes}
                  showFields={showFields}
                  showSkeleton={showSkeleton}
                  showExitedAtField={showExitedAtField}
                  showOpenAndClosedDateInputs={showOpenAndClosedDateInputs}
                  useStagedOutcomeFlow={useStagedOutcomeFlow}
                  values={values}
                />
              </div>
            </div>
          </form>
        );
      }}
    />
  );
});

CaseCloseForm.propTypes = {
  caseToClose: PropTypes.shape({
    id: PropTypes.string.isRequired,
    network: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    provider: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    group: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    state: PropTypes.string,
  }).isRequired,
  closeDialog: PropTypes.func.isRequired,
  closeServiceCase: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    full_name: PropTypes.string,
  }).isRequired,
  contactCases: PropTypes.arrayOf(
    PropTypes.shape({
      program: PropTypes.object.isRequired,
    }).isRequired,
  ),
  fetchGroupCaseOutcomes: PropTypes.func.isRequired,
  fetchNetworkCaseOutcomes: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  outcomes: PropTypes.array,
  serviceTypeId: PropTypes.string.isRequired,
  invalidateQueries: PropTypes.func.isRequired,
  crtb1349VariableAWebApp: PropTypes.bool,
  crtb1350VariableBWebApp: PropTypes.bool,
  crtb1351VariableCWebApp: PropTypes.bool,
  currentProviderId: PropTypes.string,
  initialValues: PropTypes.object,
  onSubmittingChange: PropTypes.func,
};

CaseCloseForm.defaultProps = {
  contactCases: [],
  outcomes: [],
  crtb1349VariableAWebApp: false,
  crtb1350VariableBWebApp: false,
  crtb1351VariableCWebApp: false,
  currentProviderId: '',
  initialValues: {},
  onSubmittingChange: undefined,
};

CaseCloseForm.displayName = 'CaseCloseForm';

function mapStateToProps(state, ownProps) {
  const contact = ownProps.contact;
  const contactCaseObj = get(state.serviceCase, contact.id);
  const contactCases = get(contactCaseObj, 'data', []);
  const caseToClose = ownProps.serviceCase;
  const isNetworkCase = !isEmpty(caseToClose?.network);

  const serviceTypeOutcomes = get(state, [
    'outcomes',
    isNetworkCase ? 'networkOutcomes' : 'groupOutcomes',
  ], []);

  const serviceTypeId = get(caseToClose, 'service_type.id');
  const openedDate = get(caseToClose, 'opened_date');
  const outcomesList = getOutcomesForServiceType(serviceTypeId, serviceTypeOutcomes);
  const currentProviderId = get(state, 'globalState.currentEmployee.provider.id', undefined);

  return {
    groupId: get(state, 'session.groupId'),
    contact,
    contactCases,
    caseToClose,
    crtb1349VariableAWebApp: crtb1349VariableAWebAppSelector(state),
    crtb1350VariableBWebApp: crtb1350VariableBWebAppSelector(state),
    crtb1351VariableCWebApp: crtb1351VariableCWebAppSelector(state),
    outcomes: outcomesList,
    initialValues: {
      exited_at: today(),
      opened_date: openedDate,
    },
    serviceTypeId,
    currentProviderId,
  };
}

export default connect(
  mapStateToProps,
  {
    closeServiceCase,
    fetchGroupCaseOutcomes,
    fetchNetworkCaseOutcomes,
  },
  null,
  { withRef: true },
)(CaseCloseForm);

// Keep old export name for backwards compatibility
export { CaseCloseForm as CloseCase };
